import {
  Followup,
  FollowupsFilter,
  ListFollowupsRequest,
  ListFollowupsResponse,
} from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import { Vertical } from '@weave/schema-gen-ts/dist/shared/vertical/vertical.pb';
import * as CallIntelTypes from '../types';
import { demoDataUtils } from '../utils/demo-data-utils';
import {
  categories,
  getRandomContactType,
  getRandomDirection,
  getRandomSentiment,
  officeUsers,
  phoneNumbers,
  taskTypes as allowedTaskTypes,
} from './call-recordings';
import { getAppointmentTypesForVertical } from './overview-data';

interface GenerateMockFollowUpsProps {
  persons: Person[];
  count: number;
  vertical?: Vertical | null;
  locationId?: string;
}
export class MockCallFollowUps {
  followUps: CallIntelTypes.FollowUp[] = [];

  constructor(config: GenerateMockFollowUpsProps) {
    this.generateFollowUps(config);
  }

  private generateFollowUps({ persons, count, vertical, locationId }: GenerateMockFollowUpsProps) {
    const recordsLength = Math.min(count, persons.length);

    this.followUps = persons.slice(0, recordsLength).map((person) => {
      const totalCalls = demoDataUtils.generateRandomInt(1, 3); // Maximum 3 calls per follow-up

      const calls = generateFollowUpCalls({
        person,
        vertical,
        locationId,
      });

      return {
        calls,
        totalTasks: calls.reduce((sum, call) => sum + (call?.taskCount ?? 0), 0),
        totalCalls,
      };
    });
  }

  getFilteredCallFollowUpsResponse(request: ListFollowupsRequest): ListFollowupsResponse {
    const { limit = 10, offset = 0 } = request;
    const filters = extractFollowupFilterFromRequest(request);

    const filteredFollowUps = ([...this.followUps] as Followup[]).reduce<Followup[]>((acc, followUp) => {
      const { calls = [] } = followUp;
      const filteredCalls = calls.filter((call) => {
        const isMatchingTaskType = filters.taskType
          ? filters.taskType.some((taskType) => call.taskTypes?.includes(taskType))
          : true;

        const isMatchingContactType = filters.contactType
          ? filters.contactType.some((contactType) => call.contactType === contactType)
          : true;

        return isMatchingTaskType && isMatchingContactType;
      });
      if (filteredCalls.length > 0) {
        acc.push({ ...followUp, calls: filteredCalls });
      }
      return acc;
    }, []);

    return {
      followUps: filteredFollowUps.slice(offset, offset + limit),
      totalFollowUps: filteredFollowUps.length,
    };
  }
}

const generateFollowUpCalls = ({
  person,
  vertical = Vertical.INVALID_VERTICAL,
  locationId,
}: {
  person: Person;
  vertical?: Vertical | null;
  locationId?: string;
}): CallIntelTypes.Call[] => {
  const officePhoneNumber =
    person?.contactInfo?.find(({ type }) => type === ContactType_Enum.PHONE_WORK)?.destination ??
    demoDataUtils.getRandomElement(phoneNumbers);

  const phoneNumber =
    person?.contactInfo?.find(({ type }) => type === ContactType_Enum.PHONE_HOME)?.destination ??
    demoDataUtils.getRandomElement(phoneNumbers);

  return Array.from({ length: 3 }, (_, idx) => {
    const taskCount = demoDataUtils.generateRandomInt(1, allowedTaskTypes.length);
    const taskTypes = demoDataUtils.getRandomUniqueElements(allowedTaskTypes, taskCount);
    return {
      appointmentTypes: [
        demoDataUtils.getRandomElement(getAppointmentTypesForVertical(vertical || Vertical.INVALID_VERTICAL)),
      ],
      categories: [demoDataUtils.getRandomElement(categories)],
      contactType: getRandomContactType(),
      direction: getRandomDirection(),
      id: demoDataUtils.generateRandomUUID(),
      locationId: locationId || `location-${idx}`,
      mosScore: demoDataUtils.generateRandomInt(1, 5),
      officeNumber: {
        countryCode: 1,
        nationalNumber: +officePhoneNumber,
      },
      officeUser: demoDataUtils.getRandomElement(officeUsers),
      person: {
        firstName: person.firstName,
        id: person.personId,
        lastName: person.lastName,
      },
      phoneNumber: {
        countryCode: 1,
        nationalNumber: +phoneNumber,
      },
      schedulingOpportunity: true,
      schedulingOutcome: demoDataUtils.getRandomElement([true, false]),
      sentiment: getRandomSentiment(),
      startTime: demoDataUtils.getRandomDateAndTime(),
      status: CallIntelTypes.CallStatus.CALL_STATUS_COMPLETE,
      taskCount,
      taskTypes,
    };
  });
};

const extractFollowupFilterFromRequest = (request: ListFollowupsRequest): FollowupsFilter => {
  const { filter: requestFilters = {} } = request;

  return Object.values(requestFilters).reduce<FollowupsFilter>((acc, current) => {
    Object.entries(current).forEach(([key, value]) => {
      switch (key) {
        case 'task_type':
          acc.taskType = value as CallIntelTypes.TaskType[];
          break;
        case 'task_status':
          acc.taskStatus = value as CallIntelTypes.TaskStatus[];
          break;
        case 'contact_type':
          acc.contactType = value as CallIntelTypes.ContactTypeEnum[];
          break;
        case 'locations':
          acc.locations = value as string[];
          break;
        case 'office_user_id':
          acc.officeUserId = value as string[];
          break;
        case 'date_range':
          acc.dateRange = value as CallIntelTypes.DateRange;
          break;

        default:
          break;
      }
    });
    return acc;
  }, {});
};
